import { FormMessage, Input, InputGroup } from '@aus-platform/design-system';
import { isEmpty } from 'lodash';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useInputFields } from 'shared/hooks';
import { PasswordValidationMessage } from 'src/users/components';

// Types.
export type ChangePasswordValues = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

type ChangePasswordFormProps = {
  onSubmit: (
    values: ChangePasswordValues,
    helpers: {
      reset: VoidFunction;
      setFormError: React.Dispatch<React.SetStateAction<string>>;
      setFieldErrors: React.Dispatch<
        React.SetStateAction<Record<string, string>>
      >;
    },
  ) => void;

  setIsSubmitDisabled?: (isDisabled: boolean) => void;
};

export type ChangePasswordFormRef = {
  submit: VoidFunction;
};

// Constants.
const initialState: ChangePasswordValues = {
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
};

export const ChangePasswordForm = forwardRef<
  ChangePasswordFormRef,
  ChangePasswordFormProps
>(({ onSubmit, setIsSubmitDisabled }, ref) => {
  // States.
  const [isPasswordInvalid, setIsPasswordInvalid] = useState(false);
  const [formError, setFormError] = useState('');

  // Hooks.
  const {
    values,
    names,
    errors,
    setErrors,
    onChange,
    onBlur,
    onFocus,
    inputHasError,
    inputIsDirty,
    resetAll,
  } = useInputFields<ChangePasswordValues>(initialState);

  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
  }));

  // Variables.
  const isSubmitDisabled =
    inputHasError() || !inputIsDirty() || isPasswordInvalid;

  // Effects.
  useEffect(() => {
    setIsSubmitDisabled?.(isSubmitDisabled);
  }, [isSubmitDisabled, setIsSubmitDisabled]);

  // Handlers.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event);

    const { name, value } = event.target;

    if (name === names.newPassword && !isEmpty(values.confirmNewPassword)) {
      setErrors((prev) => ({
        ...prev,
        confirmNewPassword:
          value !== values.confirmNewPassword ? "Passwords don't match." : '',
      }));
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur(event);

    const { name } = event.target;

    if (name === names.newPassword) {
      setErrors((prev) => ({
        ...prev,
        newPassword:
          !isEmpty(values.newPassword) && isPasswordInvalid ? ' ' : '',
      }));
    }
  };

  const handleSubmit = () => {
    onSubmit(values, {
      reset: resetAll,
      setFormError,
      setFieldErrors: setErrors,
    });
  };

  return (
    <div className="profile-tab__change-password__form change-password-form">
      <InputGroup>
        <Input.Label>Current Password</Input.Label>

        <Input.Password
          placeholder="Current Password"
          value={values.currentPassword}
          name={names.currentPassword}
          error={errors.currentPassword}
          isInvalid={!!errors.currentPassword}
          autoComplete="current-password"
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={onFocus}
        />
      </InputGroup>

      <InputGroup>
        <Input.Label>New Password</Input.Label>

        <Input.Password
          placeholder="New Password"
          value={values.newPassword}
          name={names.newPassword}
          error={errors.newPassword}
          isInvalid={!!errors.newPassword}
          autoComplete="new-password"
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={onFocus}
        />
      </InputGroup>

      <PasswordValidationMessage
        password={values.newPassword}
        setIsPasswordValidationError={setIsPasswordInvalid}
      />

      <InputGroup>
        <Input.Label>Confirm New Password</Input.Label>

        <Input.Password
          placeholder="Confirm New Password"
          value={values.confirmNewPassword}
          name={names.confirmNewPassword}
          error={errors.confirmNewPassword}
          isInvalid={!!errors.confirmNewPassword}
          autoComplete="new-password"
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={onFocus}
        />
      </InputGroup>

      {!isEmpty(formError) && <FormMessage message={formError} />}
    </div>
  );
});
